import { Context } from "../Engine";
import { Scene } from "../Scene";
import { Camera } from "../View";
import { ITexture } from "../Texture";
import { SceneRenderer } from "../Renderer";

export class RenderedTexture implements ITexture {
    glTexture: WebGLTexture;
    translucent: boolean;
    buffer: WebGLFramebuffer;
    private ctx: Context;

    // https://webglfundamentals.org/webgl/lessons/webgl-render-to-texture.html

    constructor() {
        this.translucent = false;
    }

    public init(ctx: Context): void {
        this.ctx = ctx;

        this.glTexture = this.createTexture();
        this.buffer = this.createFramebuffer();
    }

    private createTexture(): WebGLTexture {
        const gl = this.ctx.gl;

        // define size and format of level 0
        const level = 0;
        const internalFormat = gl.RGBA;
        const border = 0;
        const format = gl.RGBA;
        const type = gl.UNSIGNED_BYTE;
        const data = null;

        // create to render to
        const targetTextureWidth = this.ctx.viewPort.width;
        const targetTextureHeight = this.ctx.viewPort.height;

        const targetTexture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, targetTexture);

        gl.texImage2D(gl.TEXTURE_2D, level, internalFormat,
            targetTextureWidth, targetTextureHeight, border,
            format, type, data);

        // set the filtering so we don't need mips
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        return targetTexture;
    }

    private createFramebuffer(): WebGLFramebuffer {
        const gl = this.ctx.gl;

        // Create and bind the framebuffer
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);

        // attach the texture as the first color attachment
        const attachmentPoint = gl.COLOR_ATTACHMENT0;
        gl.framebufferTexture2D(gl.FRAMEBUFFER, attachmentPoint, gl.TEXTURE_2D, this.glTexture, 0);

        // create a depth renderbuffer
        const depthBuffer = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, depthBuffer);

        // make a depth buffer and the same size as the targetTexture
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, this.ctx.viewPort.width, this.ctx.viewPort.height);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depthBuffer);

        if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
            throw new Error("this combination of attachments does not work");
        }

        return fb;
    }

    public renderTexture(renderer: SceneRenderer, scene: Scene, camera: Camera): void {
        renderer.render(scene, camera, this.buffer);
    }

}